import { api } from './client'

/**
 * Modelo de WhatsApp com arquivo no cabeçalho, enviado pelo painel embutido
 * (demanda 277).
 *
 * 🔴 **A lista vem do cadastro do Chatwoot para a caixa da conversa** — é o que
 * ele aceita enviar. O arquivo sobe junto do pedido; o link que a Meta baixa é
 * montado no servidor, e o atendente nunca lida com URL.
 */
export type FormatoDoCabecalho = 'Documento' | 'Imagem' | 'Video'

export type BotaoDoModelo = {
  tipo: string
  texto: string
  /** Botão de link com `{{1}}` no endereço: a pessoa informa o final. */
  pedeParametro: boolean
}

export type ModeloComArquivo = {
  nome: string
  idioma: string
  categoria: string
  formato: FormatoDoCabecalho
  /** O que o arquivo pode ser, em palavras: "PDF", "imagem JPG ou PNG", "vídeo MP4". */
  aceita: string
  tetoEmMb: number
  corpo: string
  /** As variáveis do corpo, na ordem: `1`, `2`… ou os nomes. */
  variaveis: string[]
  botoes: BotaoDoModelo[]
}

/** A conversa de destino e os modelos da caixa dela — a tela do portal mostra os dois. */
export type ModelosDaConversa = {
  conversa: { idExterno: number; contato: string | null; caixa: string | null }
  modelos: ModeloComArquivo[]
}

/** O `accept` do seletor de arquivo — o servidor confere o tipo real, isto só ajuda a escolher. */
export const ACEITA_NO_SELETOR: Record<FormatoDoCabecalho, string> = {
  Documento: 'application/pdf,.pdf',
  Imagem: 'image/jpeg,image/png,.jpg,.jpeg,.png',
  Video: 'video/mp4,.mp4',
}

/** O estado de entrega que o Chatwoot acompanha, nas palavras da tela. */
export type SituacaoDoEnvio = 'enviada' | 'entregue' | 'lida' | 'falhou'

export type StatusDoEnvio = {
  situacao: SituacaoDoEnvio
  /** Só na falha: o motivo da Meta, já saneado pelo servidor (sem token, sem link, curto). */
  motivo: string | null
}

export const modeloComArquivo = {
  listar: (conversaExterna: number) =>
    api.get<ModelosDaConversa>(`/modelo-com-arquivo?conversa=${conversaExterna}`),

  enviar: (
    conversaExterna: number,
    modelo: ModeloComArquivo,
    arquivo: File,
    valores: Record<string, string>,
    botoes: (string | null)[],
  ) => {
    const corpo = new FormData()

    corpo.append('conversa', String(conversaExterna))
    corpo.append('modelo', modelo.nome)
    corpo.append('idioma', modelo.idioma)
    corpo.append('valores', JSON.stringify(valores))
    corpo.append('botoes', JSON.stringify(botoes))
    corpo.append('arquivo', arquivo)

    return api.enviarFormulario<{ aceito: boolean; mensagemId: number | null }>('/modelo-com-arquivo', corpo)
  },

  /**
   * O estado de entrega de um modelo que o portal enviou NESTA conversa. O servidor recusa
   * (404) mensagem que não seja um modelo enviado por ela.
   */
  status: (conversaExterna: number, mensagemId: number) =>
    api.get<StatusDoEnvio>(`/modelo-com-arquivo/status?conversa=${conversaExterna}&mensagem=${mensagemId}`),
}

/** O texto do modelo com o que já foi preenchido — o que o cliente vai ler. */
export function previa(corpo: string, valores: Record<string, string>) {
  return corpo.replace(/\{\{\s*([A-Za-z0-9_]+)\s*\}\}/g, (marcador, nome: string) =>
    valores[nome]?.trim() ? valores[nome].trim() : marcador,
  )
}
